import { notificationProvider, ThemedLayoutV2 } from "@refinedev/antd";
import "@refinedev/antd/dist/reset.css";
import { Authenticated, CanAccess, Refine } from "@refinedev/core";
import { PageNotFoundScreen } from "pages/NotFoundScreeen";
import { ForgetPassword } from "components/Auth/ForgetPassword/ForgetPassword";
import { Home } from "pages/home";
import { NewPassword } from "pages/newPassword";
import { Onboarding } from "pages/onboarding";
import { Register } from "pages/register";
import "vanilla-cookieconsent/dist/cookieconsent.css";

import {
  ApiOutlined,
  BorderOuterOutlined,
  CreditCardOutlined,
  FileTextOutlined,
  ForkOutlined,
  SettingOutlined,
  UsergroupDeleteOutlined
} from "@ant-design/icons";
import routerBindings, { CatchAllNavigate, NavigateToResource } from "@refinedev/react-router-v6";
import { DocumentIcon } from "Icons/DocumentIcon";
import { JobIcon } from "Icons/JobIcon";
import { PlaygroundIcon } from "Icons/PlaygroundIcon";
import { ConfigProvider, FloatButton } from "antd";
import { authProvider } from "authProvider";
import LoginContainer from "components/Auth/Login/LoginContainer";
import { DocumentCreate } from "components/Panel/Admin/Documents/Create";
import { DocumentEdit } from "components/Panel/Admin/Documents/Edit";
import { DocumentsList } from "components/Panel/Admin/Documents/List";
import { DocumentShow } from "components/Panel/Admin/Documents/Show";
import { DocumentationPage } from "components/Panel/ConsumerPanel/DocumentationPage/DocumentationPage";
import { Jobs } from "components/Panel/ConsumerPanel/JobsOverview/Jobs";
import { PlaygroundPage } from "components/Panel/ConsumerPanel/PlaygroundPage";
import { Documentation } from "components/Panel/ConsumerPanel/Settings";
import { SiderCS } from "components/Sider";
import { cookieStrings } from "constants/cookies";
import { ConsumerDashboardContainer } from "containers/CosumerDashboardContainer";
import dataProvider from "dataProvider";
import { AccountPendingScreen } from "pages/AccountPendingScreen";
import { AdminAPIKeys } from "pages/Admin/APIKeys";
import { AdminConfigurations } from "pages/Admin/ConfigureCredits";
import { AdminDocsEditor } from "pages/Admin/Documentation/Editor";
import { AdminDocsView } from "pages/Admin/Documentation/View";
import { AdminUserOverView } from "pages/Admin/UserOverview";
import { JobsOverview } from "pages/JobsOverview";
import { SettingsPage } from "pages/Settings";
import { DocumentationV2 } from "pages/v2/Documentation";
import { useEffect, useState } from "react";
import { BrowserRouter, Outlet, Route, Routes } from "react-router-dom";
import { antdTheme } from "theme/antd-theme";
import * as CookieConsent from "vanilla-cookieconsent";
import TermsAndConditions from "pages/TermsAndConditions";
import EndUserStatement from "pages/EndUserStatement";
import { UserIdentityData } from "types/props";
import { VacationAlert } from "components/vacationBanner";
import { liveProvider } from "live.provider";

export type User = {
  firstName: string;
  lastName: string;
  role: {
    type: string;
  };
};

function App() {
  useEffect(() => {
    CookieConsent.run({
      onFirstConsent(param) {},
      onChange(param) {
        // initGA(param);
      },
      onConsent(param) {
        // initGA(param);
      },
      categories: {
        necessary: {
          enabled: true,
          readOnly: true
        }
      },
      guiOptions: {
        consentModal: {
          position: "bottom left"
        }
      },
      language: {
        default: "en",
        translations: {
          en: {
            consentModal: {
              title: cookieStrings.title,
              description: cookieStrings.description,
              acceptAllBtn: cookieStrings.accept_all,
              acceptNecessaryBtn: cookieStrings.deny_all,
              showPreferencesBtn: cookieStrings.manage
            },
            preferencesModal: {
              title: cookieStrings.title,
              acceptAllBtn: cookieStrings.accept_all,
              acceptNecessaryBtn: cookieStrings.deny_all,
              savePreferencesBtn: cookieStrings.save_settings,
              sections: [
                {
                  title: "",
                  description: cookieStrings.description
                },
                {
                  title: cookieStrings.necessary_title,
                  description: cookieStrings.necessary_description,

                  //this field will generate a toggle linked to the 'necessary' category
                  linkedCategory: "necessary"
                }
              ]
            }
          }
        }
      }
    });
  }, []);

  return (
    <>
      <ConfigProvider
        theme={{
          token: {
            colorBgBase: "#E75012",
            colorBgContainer: "#fff",
            colorBgLayout: "#E75012",
            colorBorder: "#fff",
            colorBorderBg: "#fff"
          },
          components: {
            Select: {},
            Button: {
              colorText: "#000"
            },
            Input: {
              colorBorder: "#fff",
              colorInfoBorderHover: "#fff",
              colorBorderSecondary: "#fff",
              colorText: "#fff",
              colorBgContainer: "#fff",
              colorBorderBg: "#fff",
              activeBorderColor: "#fff",
              activeBg: "#fff"
            },
            InputNumber: {
              colorText: "#000",
              colorBgContainer: "#fff"
            }
          }
        }}>
        <FloatButton
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            zIndex: 10000
          }}
          icon={<SettingOutlined />}
          onClick={() => {
            CookieConsent.showPreferences();
          }}
        />
      </ConfigProvider>

      <BrowserRouter>
        <Refine
          dataProvider={dataProvider}
          routerProvider={routerBindings}
          liveProvider={liveProvider}
          authProvider={authProvider}
          notificationProvider={() => notificationProvider}
          accessControlProvider={{
            can: async ({ resource, action, params }) => {
              if (resource === "home-path" || resource === "settings") {
                return {
                  can: true
                };
              }
              const role = ((await authProvider?.getIdentity?.()) as UserIdentityData)?.role?.type;
              const routePrefix = resource?.split("-")?.[0];

              const isAdminRoute = routePrefix === "admin";
              const isAdmin = role === "admin";

              if (resource === "document") {
                return {
                  can: isAdmin
                };
              }

              if (resource === "documentation") {
                return {
                  can: !isAdmin
                };
              }

              return {
                can: isAdminRoute === isAdmin
              };
            }
          }}
          resources={[
            {
              name: "home-path",
              list: "/",
              meta: {
                hide: true
              }
            },
            {
              name: "settings",
              list: "/settings",
              meta: {
                hide: true
              }
            },
            {
              name: "jobs",
              list: "jobs",
              meta: {
                label: "Jobs Overview",
                icon: (
                  <div style={{ padding: "8px", marginLeft: "4px" }}>
                    <JobIcon />
                  </div>
                )
              }
            },
            {
              name: "playground",
              list: "playground",
              meta: {
                label: "Playground",
                icon: (
                  <div style={{ padding: "8px" }}>
                    <PlaygroundIcon />
                  </div>
                )
              }
            },
            {
              name: "documentation",
              list: "documentation",
              meta: {
                label: "Documentation",
                icon: (
                  <div style={{ padding: "8px" }}>
                    <DocumentIcon />
                  </div>
                )
              }
            },
            {
              name: "admin",
              list: "/admin",
              meta: {
                hide: true
              }
            },
            {
              name: "admin-jobs-overview",
              list: "/admin/jobs-overview",
              meta: {
                label: "Jobs Overview",
                icon: (
                  <div style={{ padding: "8px" }}>
                    <ForkOutlined />
                  </div>
                )
              }
            },
            {
              name: "admin-playground",
              list: "/admin/playground",
              meta: {
                label: "Playground",
                icon: (
                  <div style={{ padding: "8px" }}>
                    <BorderOuterOutlined />
                  </div>
                )
              }
            },
            {
              name: "admin-useroverview",
              list: "/admin/user-overview",
              meta: {
                label: "User Overview",
                icon: (
                  <div style={{ padding: "8px" }}>
                    <UsergroupDeleteOutlined />
                  </div>
                )
              }
            },
            {
              name: "admin-configurations",
              list: "/admin/configuration",
              meta: {
                label: "Configuration",
                icon: (
                  <div style={{ padding: "8px" }}>
                    <CreditCardOutlined />
                  </div>
                )
              }
            },
            {
              name: "admin-api-keys",
              list: "/admin/api-keys",
              meta: {
                label: "Api Keys",
                icon: (
                  <div style={{ padding: "8px" }}>
                    <ApiOutlined />
                  </div>
                )
              }
            },
            {
              name: "document",
              list: "/admin/docs",
              create: "/admin/docs/create",
              edit: "/admin/docs/edit/:id",
              show: "/admin/docs/show/:id",
              meta: {
                label: "Documents",
                icon: (
                  <div style={{ padding: "8px" }}>
                    <FileTextOutlined />
                  </div>
                ),
                canDelete: true
              }
            }
          ]}>
          <Routes>
            {/* auth routes */}
            <Route index element={<NavigateToResource resource={"jobs"} />} />
            <Route
              element={
                <Authenticated key={"auth-pages"} fallback={<Outlet />}>
                  <NavigateToResource />
                </Authenticated>
              }>
              <Route
                path="/login"
                element={
                  <ConfigProvider theme={antdTheme}>
                    <LoginContainer />
                  </ConfigProvider>
                }
              />
              <Route
                path="/register"
                element={
                  <ConfigProvider theme={antdTheme}>
                    <Register />
                  </ConfigProvider>
                }
              />
              <Route
                path="/forgot-password"
                element={
                  <ConfigProvider theme={antdTheme}>
                    <ForgetPassword />
                  </ConfigProvider>
                }
              />
              <Route
                path="/new-password"
                element={
                  <ConfigProvider theme={antdTheme}>
                    <NewPassword />
                  </ConfigProvider>
                }
              />
            </Route>
            <Route
              path="/terms-and-conditions"
              element={
                <ConfigProvider theme={antdTheme}>
                  <TermsAndConditions />
                </ConfigProvider>
              }
            />
            <Route
              path="/end-user-statement"
              element={
                <ConfigProvider theme={antdTheme}>
                  <EndUserStatement />
                </ConfigProvider>
              }
            />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route
              path="/account-pending"
              element={
                <ConfigProvider theme={antdTheme}>
                  <AccountPendingScreen />
                </ConfigProvider>
              }
            />
            {/* Admin and User routes */}
            <Route
              element={
                <Authenticated key={"all-pages"} fallback={<CatchAllNavigate to="/login" />}>
                  <ConfigProvider theme={antdTheme}>
                    <ThemedLayoutV2 Sider={(props) => <SiderCS {...props} fixed />}>
                      <CanAccess fallback={<PageNotFoundScreen />}>
                        <VacationAlert />
                        <Outlet />
                      </CanAccess>
                    </ThemedLayoutV2>
                  </ConfigProvider>
                </Authenticated>
              }>
              <Route
                index
                path="/"
                element={
                  <Authenticated key={"home"} fallback={<CatchAllNavigate to="/login" />}>
                    <Home />
                  </Authenticated>
                }
              />
              <Route path="/documentation" element={<DocumentationV2 />} />
              <Route path="/jobs" element={<Jobs />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route
                path="/settings/:setting"
                element={
                  <ConsumerDashboardContainer>
                    <Documentation />
                  </ConsumerDashboardContainer>
                }
              />
              <Route path="/playground" element={<PlaygroundPage />} />
              <Route
                path="/documentation/:id"
                element={
                  <ConsumerDashboardContainer>
                    <DocumentationPage />
                  </ConsumerDashboardContainer>
                }
              />
              <Route path="/admin" element={<AdminUserOverView />} />
              <Route path="/admin/jobs-overview" element={<JobsOverview />} />
              <Route path="/admin/playground" element={<PlaygroundPage />} />
              <Route path="/admin/user-overview" element={<AdminUserOverView />} />
              <Route path="/admin/configuration" element={<AdminConfigurations />} />
              <Route path="/admin/api-keys" element={<AdminAPIKeys />} />
              <Route path="/admin/docs">
                <Route index element={<DocumentsList />} />
                <Route path="create" element={<DocumentCreate />} />
                <Route path="edit/:id" element={<DocumentEdit />} />
                <Route path="show/:id" element={<DocumentShow />} />
              </Route>
              <Route path="/admin/docs/new" element={<AdminDocsEditor />} />
              <Route path="/admin/docs/:id" element={<AdminDocsView />} />
              <Route path="/admin/docs/:id/edit" element={<AdminDocsEditor />} />
              <Route path="*" element={<PageNotFoundScreen />} />
            </Route>
          </Routes>
        </Refine>
      </BrowserRouter>
    </>
  );
}

export default App;
